import { useQuery } from "@tanstack/react-query";
import { learningService } from "../../../api/learningService";
import Badge from "../../../components/ui/Badge";
import { getErrorMessage } from "../../../utils/apiError";
import { formatDate } from "../../../utils/date";
import { formatManilaDate } from "../../../utils/manilaTime";
import {
  bondStatus,
  paybackInWords,
  peso,
} from "../../../../utils/trainingBond";
import { LEARNING_KEY } from "../learningUtils";
import ui from "../../../components/ui/ui.module.css";

const EMPTY = [];

/** The rows of GET /learning/my/bonds (also used for the tab's count). */
export const useMyBonds = (enabled = true) =>
  useQuery({
    queryKey: [LEARNING_KEY, "bonds"],
    queryFn: learningService.getMyBonds,
    enabled,
  });

/** My training agreements: where each stands and what I would owe today. */
const MyBonds = () => {
  const query = useMyBonds();
  const rows = Array.isArray(query.data) ? query.data : EMPTY;

  const message = query.isLoading
    ? "Loading your bonds…"
    : query.isError
      ? getErrorMessage(query.error, "Unable to load your bonds.")
      : rows.length === 0
        ? "You have no training bonds."
        : "";

  return (
    <div className={ui.card}>
      <div className={ui.cardHeader}>
        <span className={ui.cardTitle}>My bonds</span>
        <span className={ui.hint}>
          For reference. Nothing is deducted from your pay automatically.
        </span>
      </div>
      <div className={ui.tableWrapper}>
        <table className={ui.table}>
          <thead>
            <tr>
              <th>Training</th>
              <th>Status</th>
              <th>Bond</th>
              <th>Ends</th>
              <th>Owed today</th>
              <th>Accepted</th>
            </tr>
          </thead>
          <tbody>
            {message && (
              <tr>
                <td
                  colSpan={6}
                  className={query.isError ? "text-danger" : ui.muted}
                >
                  {message}
                </td>
              </tr>
            )}
            {rows.map((b) => {
              const meta = bondStatus(b.status);
              const owes = b.status === "ACTIVE" || b.status === "SEPARATED";
              return (
                <tr key={b.id}>
                  <td className={ui.strong}>{b.seminar?.title ?? "—"}</td>
                  <td>
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                  </td>
                  <td title={paybackInWords(b.paybackRule, b.cost, b.months)}>
                    {peso(b.cost)} · {b.months} months
                  </td>
                  <td className={ui.noWrap}>
                    {b.endsOn ? formatDate(b.endsOn) : "—"}
                  </td>
                  <td className={ui.noWrap}>{owes ? peso(b.balance) : "—"}</td>
                  <td className={ui.noWrap}>
                    {b.acceptedAt ? formatManilaDate(b.acceptedAt) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MyBonds;
